import { consume, ContextType } from '@lit/context';
import { css, html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import ms from 'ms';
import { contexts } from '../contexts.js';
import { FINISH_DURATION } from './fk-progress.js';
import { ProjectAlerts } from './project-alerts.js';

// Most loads finish inside a second and should never replace the content at
// all. Past that the wait is long enough to be worth acknowledging, and a
// screen that did appear should not be yanked away the instant the data lands.
const REVEAL_DELAY = ms('1 second');
const MIN_VISIBLE_DURATION = ms('1 second');
const FADE_OUT_DURATION = ms('200ms');

@customElement('project-body')
class ProjectBody extends LitElement {
  @property({ type: Boolean }) wide?: boolean;

  @consume({ context: contexts.dataSnapshotInfo, subscribe: true }) @state()
  private _dataSnapshotInfo?: ContextType<typeof contexts.dataSnapshotInfo>;

  // Every project page is wrapped in this element, so alerts are rendered here
  // rather than repeated at each call site.
  @consume({ context: contexts.project, subscribe: true }) @state()
  private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.serverInfo, subscribe: true }) @state()
  private _serverInfo?: ContextType<typeof contexts.serverInfo>;
  @consume({ context: contexts.dataSnapshotId, subscribe: true }) @state()
  private _snapshotId?: ContextType<typeof contexts.dataSnapshotId>;

  private _transitionTimeout?: ReturnType<typeof setTimeout>;
  private _transitionAt?: number;

  /**
   * The load laid out on the wall clock, or undefined when there is nothing to
   * show — no load, or one that finished before the screen was due to appear.
   * Everything else is read off this, so there is no state to keep in sync.
   */
  private _schedule() {
    const startedAt = this._dataSnapshotInfo?.loadingStarted;
    if (startedAt === undefined || this._dataSnapshotInfo?.loadingMode === 'refresh')
      return undefined;
    const loadedTimestamp = this._dataSnapshotInfo?.loadingFinished;
    const loadedAt = loadedTimestamp !== undefined && loadedTimestamp >= startedAt ? loadedTimestamp : undefined;
    const revealAt = startedAt + REVEAL_DELAY;
    if (loadedAt !== undefined && loadedAt < revealAt)
      return undefined;
    if (loadedAt === undefined)
      return { startedAt, revealAt, finishAt: undefined, fadeAt: undefined, goneAt: undefined };
    const finishAt = Math.max(loadedAt, revealAt + MIN_VISIBLE_DURATION);
    const fadeAt = finishAt + FINISH_DURATION;
    return { startedAt, revealAt, finishAt, fadeAt, goneAt: fadeAt + FADE_OUT_DURATION };
  }

  override render() {
    const alerts = ProjectAlerts.maybeRender(this._project, this._snapshotId, this._serverInfo);
    const schedule = this._schedule();
    const now = Date.now();
    if (schedule === undefined || now < schedule.revealAt || (schedule.goneAt !== undefined && now >= schedule.goneAt))
      return html`<app-body ?wide=${this.wide}>${alerts}<slot></slot></app-body>`;
    return html`
      <app-body ?wide=${this.wide}>
        ${alerts}
        <div class=loading role=status aria-live=polite>
          <div class="message ${schedule.fadeAt !== undefined && now >= schedule.fadeAt ? 'leaving' : ''}"
               style="--fade-out-duration: ${FADE_OUT_DURATION}ms">
            <div class=logo aria-hidden=true>
              <img src=${window.location.hostname.includes('.test') ? '/logodev.svg' : '/logo.svg'}>
            </div>
            <fk-progress aria-hidden=true .startedAt=${schedule.startedAt} .finishAt=${schedule.finishAt}></fk-progress>
            <div class=title>Loading project data…</div>
            <div class=subtitle>Building your point-in-time workspace</div>
          </div>
        </div>
      </app-body>
    `;
  }

  // Nothing else will tell us the clock has passed a boundary, so arm a single
  // timer for the next one. Keyed on the moment it fires, so the repeated
  // renders that come with context updates re-use the pending timer instead of
  // piling up new ones.
  override updated() {
    const schedule = this._schedule();
    const now = Date.now();
    let transitionAt: number | undefined;
    if (schedule && now < schedule.revealAt)
      transitionAt = schedule.revealAt;
    else if (schedule?.fadeAt !== undefined && now < schedule.fadeAt)
      transitionAt = schedule.fadeAt;
    else if (schedule?.goneAt !== undefined && now < schedule.goneAt)
      transitionAt = schedule.goneAt;
    if (transitionAt === this._transitionAt)
      return;
    clearTimeout(this._transitionTimeout);
    this._transitionAt = transitionAt;
    this._transitionTimeout = transitionAt === undefined ? undefined :
      setTimeout(() => this.requestUpdate(), transitionAt - now);
  }

  override disconnectedCallback() {
    clearTimeout(this._transitionTimeout);
    super.disconnectedCallback();
  }

  static styles = css`
    :host {
      display: contents;
    }

    .loading {
      display: grid;
      min-height: clamp(20rem, 58vh, 42rem);
      place-items: center;
    }

    .message {
      color: var(--sl-color-neutral-500);
      text-align: center;
      animation: reveal 180ms ease-out forwards;
    }

    .message.leaving {
      animation: fade-out var(--fade-out-duration) ease-in forwards;
    }

    .logo {
      width: 88px;
      height: 88px;
      margin: 0 auto var(--sl-spacing-large);
    }

    .logo img {
      width: 100%;
      height: 100%;
    }

    fk-progress {
      /* Wider than the caption below it, so the bar sets the column width
         instead of being the narrowest thing in the stack. */
      width: 240px;
      height: 6px;
      margin: 0 auto var(--sl-spacing-large);
    }

    .title {
      color: var(--sl-color-neutral-700);
      font-size: var(--sl-font-size-large);
      font-weight: var(--sl-font-weight-semibold);
    }

    .subtitle {
      margin-top: var(--sl-spacing-2x-small);
      font-size: var(--sl-font-size-small);
    }

    @keyframes reveal {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes fade-out {
      from { opacity: 1; }
      to { opacity: 0; }
    }
  `;
}
